import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = "/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // "verifying", "form", "success", "error"
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({});

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid reset link. No token provided.");
      return;
    }

    verifyToken(token);
  }, [token]);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API}/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
      
      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error("Invalid or expired reset link");
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Invalid or expired reset link");
      }

      setEmail(data.email);
      setStatus("form");
    } catch (error) {
      setStatus("error");
      setErrorMessage(error.message);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token,
          password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to reset password");
      }

      setStatus("success");
      toast.success("Password reset successful!");

      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 3000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
          {/* Verifying State */}
          {status === "verifying" && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Verifying Link</h1>
              <p className="text-muted-foreground">
                Please wait while we verify your reset link...
              </p>
            </div>
          )}

          {/* Form State */}
          {status === "form" && (
            <>
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-2">Reset Your Password</h1>
                <p className="text-muted-foreground text-sm">
                  Enter a new password for <span className="font-medium text-foreground">{email}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`pl-10 pr-10 h-12 rounded-xl ${errors.password ? 'border-red-500' : ''}`}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className={`pl-10 h-12 rounded-xl ${errors.confirmPassword ? 'border-red-500' : ''}`}
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Success State */}
          {status === "success" && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-green-600 dark:text-green-400">
                Password Reset!
              </h1>
              <p className="text-muted-foreground mb-6">
                Your password has been successfully reset. You can now login with your new password.
              </p>
              
              <div className="bg-muted/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-muted-foreground">
                  Redirecting you to login...
                </p>
              </div>
              
              <Button 
                onClick={() => navigate("/")} 
                className="w-full h-12 rounded-xl"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </div>
          )}

          {/* Error State */}
          {status === "error" && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-red-600 dark:text-red-400">
                Link Invalid
              </h1>
              <p className="text-muted-foreground mb-6">
                {errorMessage}
              </p>
              
              <div className="bg-muted/50 rounded-xl p-4 mb-6 text-sm text-muted-foreground">
                <p>This can happen if:</p>
                <ul className="list-disc list-inside mt-2 text-left">
                  <li>The link has expired (links are valid for 1 hour)</li>
                  <li>The link has already been used</li>
                  <li>The link was copied incorrectly</li>
                </ul>
              </div>
              
              <Button 
                onClick={() => navigate("/")} 
                className="w-full h-12 rounded-xl"
                data-testid="go-home-btn"
              >
                Go to Home
              </Button>
            </div>
          )}
        </div>

        {/* Branding */}
        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-primary">CodingDrills</span>.au
          </p>
        </div>
      </div>
    </div>
  );
}
